import { getApiBaseUrl } from '../lib/api';
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Brain,
  Users,
  Home,
  Wrench,
  FileText,
  AlertTriangle,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Play,
  Zap,
  BarChart3,
  Activity,
  Target,
  Eye,
  Shield,
  Scale,
  Gavel,
  Bot,
  Network,
  GitBranch,
  Database,
  Layers,
  Settings,
  Info,
  ArrowRight,
  Building2,
  Briefcase
} from 'lucide-react';

const API = getApiBaseUrl();

// Operations Team Card (hiding AI nature)
const OperationsCard = ({ name, displayName, status, capabilities, autonomyLevel }) => {
  const statusColors = {
    idle: 'bg-green-100 text-green-800',
    processing: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800'
  };

  const operationIcons = {
    sarah_screening: Users,
    james_rent: TrendingUp,
    emma_legal: Scale,
    alex_compliance: Shield,
    denise_support: Wrench,
    communication: FileText
  };

  const friendlyNames = {
    sarah_screening: "Tenant Screening",
    james_rent: "Rent Management",
    emma_legal: "Legal Operations",
    alex_compliance: "Compliance",
    denise_support: "Maintenance",
    communication: "Communications"
  };

  const OpIcon = operationIcons[name] || Briefcase;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-blue-100 text-blue-600">
              <OpIcon className="h-5 w-5" />
            </div>
            <div>
              <h4 className="font-semibold text-sm">{friendlyNames[name] || displayName}</h4>
              <p className="text-xs text-gray-500">Status: Active</p>
            </div>
          </div>
          <Badge className={statusColors[status] || statusColors.idle}>
            {status === 'idle' ? 'Ready' : status}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

// Scottish Eviction Ground Card
const GroundCard = ({ ground }) => (
  <div className="p-3 border rounded-lg hover:bg-gray-50">
    <div className="flex items-center justify-between mb-2">
      <span className="font-medium text-sm">{ground.id.toUpperCase()}</span>
      <Badge variant={ground.mandatory ? "default" : "secondary"}>
        {ground.mandatory ? "Mandatory" : "Discretionary"}
      </Badge>
    </div>
    <p className="text-sm text-gray-700 mb-2">{ground.name}</p>
    <div className="flex items-center gap-2 text-xs text-gray-500">
      <Clock className="h-3 w-3" />
      {ground.notice_period_days} days notice
    </div>
  </div>
);

// Collaboration Protocol Card
const ProtocolCard = ({ protocol, onStart }) => {
  const typeIcons = {
    sequential: GitBranch,
    parallel: Layers,
    consensus: Users
  };
  const Icon = typeIcons[protocol.type] || Network;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-full bg-purple-100 text-purple-600">
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm">{protocol.display_name}</h4>
            <p className="text-xs text-gray-500">{protocol.type} • {protocol.steps_count} steps</p>
          </div>
        </div>
        <p className="text-xs text-gray-600 mb-3">{protocol.description}</p>
        <Button variant="outline" size="sm" className="w-full" onClick={() => onStart(protocol.name)}>
          <Play className="h-3 w-3 mr-2" /> Start Protocol
        </Button>
      </CardContent>
    </Card>
  );
};

// Autonomy Level Display
const AutonomyLevelCard = ({ level }) => {
  const levelColors = ['bg-gray-100', 'bg-blue-100', 'bg-green-100', 'bg-yellow-100', 'bg-red-100'];
  
  return (
    <div className={`p-3 rounded-lg ${levelColors[level.level] || 'bg-gray-100'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-semibold">L{level.level}</span>
        <span className="text-sm">{level.name}</span>
      </div>
      <p className="text-xs text-gray-600 mb-1">{level.description}</p>
      <p className="text-xs text-gray-500 italic">{level.human_interaction}</p>
    </div>
  );
};

// Main Dashboard
const AIAgentsDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState(null);
  const [legalFramework, setLegalFramework] = useState(null);
  const [grounds, setGrounds] = useState([]);
  const [autonomyLevels, setAutonomyLevels] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [collabStats, setCollabStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('scotland');
  const [allJurisdictions, setAllJurisdictions] = useState(null);

  const fetchData = useCallback(async (jurisdiction = 'scotland') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch all data in parallel
      const [statusRes, frameworkRes, groundsRes, autonomyRes, protocolsRes, statsRes, jurisdictionsRes] = await Promise.all([
        fetch(`${API}/api/ai-agents/status`, { headers }),
        fetch(`${API}/api/governance/legal-framework?jurisdiction=${jurisdiction}`, { headers }),
        fetch(`${API}/api/ai-agents/legal/grounds?jurisdiction=${jurisdiction}`, { headers }),
        fetch(`${API}/api/ai-agents/autonomy/levels`, { headers }),
        fetch(`${API}/api/collaboration/protocols`, { headers }),
        fetch(`${API}/api/collaboration/statistics`, { headers }),
        fetch(`${API}/api/governance/jurisdictions`, { headers })
      ]);

      if (statusRes.ok) {
        setSystemStatus(await statusRes.json());
      }
      if (frameworkRes.ok) {
        setLegalFramework(await frameworkRes.json());
      }
      if (groundsRes.ok) {
        const data = await groundsRes.json();
        setGrounds(data.grounds || []);
      }
      if (autonomyRes.ok) {
        const data = await autonomyRes.json();
        setAutonomyLevels(data.levels || []);
      }
      if (protocolsRes.ok) {
        const data = await protocolsRes.json();
        setProtocols(data.protocols || []);
      }
      if (statsRes.ok) {
        setCollabStats(await statsRes.json());
      }
      if (jurisdictionsRes.ok) {
        const data = await jurisdictionsRes.json();
        setAllJurisdictions(data.jurisdictions);
      }
    } catch (error) {
      console.error('Error fetching AI data:', error);
      toast.error('Failed to load AI system data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedJurisdiction);
  }, [fetchData, selectedJurisdiction]);

  const handleJurisdictionChange = (jurisdiction) => {
    setSelectedJurisdiction(jurisdiction);
  };

  const handleStartProtocol = async (protocolName) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/api/collaboration/protocols/${protocolName}/start`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ context: { test: true } })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Started ${protocolName} workflow: ${data.workflow_id}`);
        fetchData();
      } else {
        toast.error('Failed to start protocol');
      }
    } catch (error) {
      console.error('Error starting protocol:', error);
      toast.error('Failed to start protocol');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="h-7 w-7 text-blue-600" />
            Operations Centre
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Property management intelligence for UK lettings
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Jurisdiction Selector */}
          <select
            value={selectedJurisdiction}
            onChange={(e) => handleJurisdictionChange(e.target.value)}
            className="px-3 py-1.5 text-sm border rounded-lg bg-white"
          >
            <option value="scotland">🏴󠁧󠁢󠁳󠁣󠁴󠁿 Scotland</option>
            <option value="england">🏴󠁧󠁢󠁥󠁮󠁧󠁿 England</option>
            <option value="wales">🏴󠁧󠁢󠁷󠁬󠁳󠁿 Wales</option>
          </select>
          <Badge variant={systemStatus?.system_status === 'operational' ? 'default' : 'destructive'}>
            {systemStatus?.system_status === 'operational' ? 'Online' : 'Offline'}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => fetchData(selectedJurisdiction)}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Legal Framework Banner */}
      {legalFramework && (
        <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                <Gavel className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">
                  {selectedJurisdiction.charAt(0).toUpperCase() + selectedJurisdiction.slice(1)} Legal Framework
                </h3>
                <p className="text-sm text-blue-700">{legalFramework.governing_act}</p>
                <p className="text-xs text-blue-600 mt-1">
                  {selectedJurisdiction === 'scotland' ? 'Tribunal' : 'Court'}: {legalFramework.tribunal}
                </p>
                {legalFramework.tenancy_type && (
                  <p className="text-xs text-blue-500 mt-1">
                    Tenancy Type: {legalFramework.tenancy_type}
                  </p>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{legalFramework.eviction_grounds_count}</div>
                <div className="text-xs text-blue-500">Eviction Grounds</div>
                <div className="text-xs text-gray-500 mt-1">
                  {legalFramework.mandatory_grounds_count} Mandatory • {legalFramework.discretionary_grounds_count} Discretionary
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">
            <Activity className="h-4 w-4 mr-2" /> Overview
          </TabsTrigger>
          <TabsTrigger value="agents">
            <Briefcase className="h-4 w-4 mr-2" /> Operations
          </TabsTrigger>
          <TabsTrigger value="governance">
            <Shield className="h-4 w-4 mr-2" /> Governance
          </TabsTrigger>
          <TabsTrigger value="collaboration">
            <Network className="h-4 w-4 mr-2" /> Workflows
          </TabsTrigger>
          <TabsTrigger value="legal">
            <Scale className="h-4 w-4 mr-2" /> Legal Framework
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <Briefcase className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold">
                  {Object.keys(systemStatus?.agents || {}).length}
                </div>
                <div className="text-sm text-gray-500">Operations Active</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Network className="h-8 w-8 mx-auto mb-2 text-purple-600" />
                <div className="text-2xl font-bold">{protocols.length}</div>
                <div className="text-sm text-gray-500">Workflows</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Scale className="h-8 w-8 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold">{grounds.length}</div>
                <div className="text-sm text-gray-500">Legal Grounds</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <Shield className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                <div className="text-2xl font-bold">{autonomyLevels.length}</div>
                <div className="text-sm text-gray-500">Approval Levels</div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="h-5 w-5" /> Queue Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-xl font-bold text-yellow-600">
                      {systemStatus?.queue?.pending || 0}
                    </div>
                    <div className="text-xs text-gray-500">Pending</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-600">
                      {systemStatus?.queue?.in_progress || 0}
                    </div>
                    <div className="text-xs text-gray-500">In Progress</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xl font-bold text-green-600">
                      {systemStatus?.queue?.completed_today || 0}
                    </div>
                    <div className="text-xs text-gray-500">Completed Today</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <GitBranch className="h-5 w-5" /> Collaboration Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-xl font-bold text-purple-600">
                      {collabStats?.total_workflows || 0}
                    </div>
                    <div className="text-xs text-gray-500">Total Workflows</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-xl font-bold text-green-600">
                      {collabStats?.by_status?.completed || 0}
                    </div>
                    <div className="text-xs text-gray-500">Completed</div>
                  </div>
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-xl font-bold text-blue-600">
                      {collabStats?.protocols_available || 0}
                    </div>
                    <div className="text-xs text-gray-500">Protocols</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Agents Tab */}
        <TabsContent value="agents">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" /> Operations Team
              </CardTitle>
              <CardDescription>
                Specialized operations handling different aspects of property management
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(systemStatus?.agents || {}).map(([key, agent]) => (
                  <OperationsCard
                    key={key}
                    name={key}
                    displayName={agent.name}
                    status={agent.status}
                    capabilities={agent.capabilities}
                    autonomyLevel={agent.autonomy_level}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Approval Levels */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" /> Approval Levels
              </CardTitle>
              <CardDescription>
                Defining when human approval is required for decisions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {autonomyLevels.map((level) => (
                  <AutonomyLevelCard key={level.level} level={level} />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Governance Tab */}
        <TabsContent value="governance">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" /> Governance & Safety Kernel
                </CardTitle>
                <CardDescription>
                  Compliance layer validating all agent actions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-700">Policy Engine Active</span>
                    </div>
                    <p className="text-sm text-green-600">
                      All agent actions validated against Scottish tenancy law
                    </p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Info className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-blue-700">Explainability Packets</span>
                    </div>
                    <p className="text-sm text-blue-600">
                      Every AI decision generates an immutable audit trail
                    </p>
                  </div>
                  
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-purple-600" />
                      <span className="font-semibold text-purple-700">Decision Framework</span>
                    </div>
                    <p className="text-sm text-purple-600">
                      RL-based policy optimization with human oversight
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" /> MLOps Platform
                </CardTitle>
                <CardDescription>
                  Model registry, training, and monitoring
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Model Registry</span>
                    <Badge>Active</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Training Pipeline</span>
                    <Badge>Active</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Drift Monitoring</span>
                    <Badge>Active</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Label Store</span>
                    <Badge>8 Definitions</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm">Feature Store</span>
                    <Badge>Active</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Collaboration Tab */}
        <TabsContent value="collaboration">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Network className="h-5 w-5" /> Multi-Agent Collaboration Protocols
              </CardTitle>
              <CardDescription>
                Predefined workflows for complex multi-agent tasks
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {protocols.map((protocol) => (
                  <ProtocolCard
                    key={protocol.name}
                    protocol={protocol}
                    onStart={handleStartProtocol}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Legal Tab - Multi-Jurisdiction */}
        <TabsContent value="legal">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="h-5 w-5" /> 
                {selectedJurisdiction.charAt(0).toUpperCase() + selectedJurisdiction.slice(1)} Eviction Grounds
              </CardTitle>
              <CardDescription>
                {selectedJurisdiction === 'scotland' && 'Schedule 3 of Private Housing (Tenancies) (Scotland) Act 2016'}
                {selectedJurisdiction === 'england' && 'Schedule 2 of Housing Act 1988 (as amended)'}
                {selectedJurisdiction === 'wales' && 'Renting Homes (Wales) Act 2016'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Key differences info */}
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Info className="h-4 w-4 text-amber-600" />
                  <span className="font-medium text-amber-800">Key Features for {selectedJurisdiction.charAt(0).toUpperCase() + selectedJurisdiction.slice(1)}</span>
                </div>
                <ul className="text-xs text-amber-700 space-y-1">
                  {legalFramework?.key_features?.slice(0, 3).map((feature, i) => (
                    <li key={i}>• {feature}</li>
                  ))}
                </ul>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {grounds.map((ground) => (
                  <GroundCard key={ground.id} ground={ground} />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIAgentsDashboard;
